/**
 * Allowlist configuration for redirect URLs.
 */
export const REDIRECT_ALLOWLIST = {
  paths: ['/scribe', '/g2-review'],
  queryParams: {
    action: ['create-gpccmp', 'activate-evidence', 'upgrade'],
  },
} as const;

export type DeeplinkAction = (typeof REDIRECT_ALLOWLIST.queryParams.action)[number];

export const isValidDeeplinkAction = (action: unknown): action is DeeplinkAction => {
  return (
    typeof action === 'string' &&
    (REDIRECT_ALLOWLIST.queryParams.action as readonly string[]).includes(action)
  );
};

// `REDIRECT_ALLOWLIST.paths` only exact-matches fixed routes, so a dynamic
// segment like a task id needs its own pattern. Shared by `getSafeRedirectUrl`
// (login-page consumer) and `middleware.ts` (isReturnToCurrentPage) so the two
// allowlists can't drift apart the way `RETURN_TO_SELF_ROUTES`'s comment warns
// against for the fixed paths.
const TASK_DEEP_LINK_PATH_PATTERN = /^\/tasks\/[A-Za-z0-9_-]{1,100}$/;

export const isTaskDeepLinkPath = (pathname: string): boolean =>
  TASK_DEEP_LINK_PATH_PATTERN.test(pathname);

export const getSanitizedAction = (action: unknown): DeeplinkAction | null => {
  return isValidDeeplinkAction(action) ? action : null;
};

/**
 * Sanitizes and validates a redirect URL against an allowlist.
 * Returns a reconstructed safe URL or null if invalid.
 * Allowed params: 'action' (single, validated) and 'utm_*' (pass-through for tracking).
 * Invalid actions are ignored (only the first action param is considered).
 * Hash/fragments are stripped (not included in output)
 */
export const getSafeRedirectUrl = (
  queryValue: string | string[] | undefined
): string | null => {
  const url = typeof queryValue === 'string' ? queryValue : undefined;
  if (!url) return null;
  if (!url.startsWith('/') || url.startsWith('//') || url.includes('\\')) return null;

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url, 'http://localhost');
  } catch {
    return null;
  }

  const { pathname, searchParams } = parsedUrl;

  const isAllowedPath =
    REDIRECT_ALLOWLIST.paths.includes(
      pathname as (typeof REDIRECT_ALLOWLIST.paths)[number]
    ) || isTaskDeepLinkPath(pathname);

  if (!isAllowedPath) {
    return null;
  }

  const allParams = [...searchParams.entries()];

  const sanitizedParams = new URLSearchParams();
  const action = searchParams.get('action');
  if (isValidDeeplinkAction(action)) {
    sanitizedParams.set('action', action);
  }
  allParams
    .filter(([key]) => key.startsWith('utm_'))
    .forEach(([key, value]) => sanitizedParams.append(key, value));

  const search = sanitizedParams.toString();
  return search ? `${pathname}?${search}` : pathname;
};
